/**
 * FICHES DE LOCAL (§6.3) — ce qui compte comme « renseigné », et ce qui manque.
 *
 * ── Le défaut que ce module ferme ──────────────────────────────────────────
 *
 * L'écran signalait « fiche non renseignée » quand l'OBJET était absent. Or il
 * suffit d'ouvrir l'éditeur d'une pièce et d'enregistrer pour qu'un objet
 * existe, avec tous ses champs vides : l'avertissement disparaît alors, et le
 * panneau n'affiche plus qu'« Aucune surveillance » — ce qui se lit comme une
 * réponse complète. Une fiche vide devient une fiche remplie par le simple fait
 * qu'on l'a effleurée.
 *
 * ⚠️ C'est la même famille de défaut que BLOQ-03 : **un état vide est une
 * AFFIRMATION**. Ici l'affirmation serait « ce local a été décrit », et rien à
 * l'écran ne permettrait de la démentir.
 *
 * ── Et pourquoi `usage` seul fait foi ──────────────────────────────────────
 *
 * §6.3 demande que les locaux soient adaptés à ce qu'on y fait ; sans l'usage,
 * aucune des autres rubriques n'a de sens — « conditions requises » pour quoi
 * faire, « séparé de » au nom de quoi. Les autres rubriques peuvent
 * légitimement rester vides : un bureau n'a pas de risque particulier à
 * déclarer, et l'écrire « néant » n'apprend rien.
 *
 * ⚠️ `surveillance` NE COMPTE PAS comme un remplissage. Sa valeur par défaut
 * est « aucune », qui se trouve être la bonne réponse pour toutes les pièces au
 * 27/08/2026 — donc un champ jamais touché est indiscernable d'une réponse
 * réfléchie. Un défaut qui tombe juste reste un défaut : on ne peut pas le
 * compter comme une déclaration.
 */

export type FicheLocalLue = {
  usage?: string;
  conditionsRequises?: string;
  surveillance?: string;
  separationDe?: string;
  accesRestreint?: string;
  risques?: string;
} | null | undefined;

/** Le minimum que §6.3 attend d'une pièce : à quoi elle sert. */
export function ficheRenseignee(local: FicheLocalLue): boolean {
  return !!String(local?.usage || '').trim();
}

/**
 * Une fiche EXISTE mais ne dit rien : le cas qui se lisait « renseignée ».
 * Distinct de « pas de fiche » à l'écran, parce que les deux appellent le même
 * geste mais pas la même phrase — « commencez » et « terminez » ne se disent
 * pas à la même personne.
 */
export function ficheEffleuree(local: FicheLocalLue): boolean {
  return !!local && !ficheRenseignee(local);
}

export type PieceLue = {
  code: string; libelle?: string; niveau?: string; local?: FicheLocalLue;
  /** Les codes des nœuds parents, du plus haut au plus proche. Sert à DÉPLIER
   *  l'arbre jusqu'à la pièce : sauter à un nœud replié ne montre rien. */
  chemin?: string[];
};

/**
 * Les pièces dont la fiche manque, dans l'ordre où elles sont données. Seules
 * les PIÈCES sont concernées : un étage ou un couloir n'a pas de fiche de local.
 */
export function piecesSansFiche(noeuds: PieceLue[]): PieceLue[] {
  return (noeuds || []).filter((n) => n.niveau === 'piece' && !ficheRenseignee(n.local));
}

/**
 * La phrase du bandeau. Elle est ici, et pas dans le composant, parce qu'elle
 * ÉNONCE UN ÉTAT DE CONFORMITÉ : c'est une règle, elle se teste.
 *
 * ⚠️ Elle ne dit jamais « 3 fiches renseignées » sans dire sur combien. Un
 * numérateur seul se lit comme un progrès ; c'est le dénominateur qui dit qu'il
 * reste seize pièces.
 */
export function phraseFiches(noeuds: PieceLue[]): string {
  const pieces = (noeuds || []).filter((n) => n.niveau === 'piece');
  if (!pieces.length) return '';
  const sans = piecesSansFiche(pieces);
  if (!sans.length) {
    return `Les ${pieces.length} pièces ont leur fiche de local (§6.3).`;
  }
  return `${sans.length} pièce(s) sur ${pieces.length} n’ont pas de fiche de local (§6.3).`;
}
